import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState, useSyncExternalStore } from "react";

import cloneDeep from "lodash/cloneDeep";
import merge from "lodash/merge";

import useMemoDeep from "Utilities/Hooks/useMemoDeep";

import flocknoteNetworkApi from "./API/fetch";
import { flocknoteKeys } from "./API/keys";
import {
  useGetNetworkMetadata,
  useGetPermissions,
  useGetUserData,
  useGetUserFundItPermissions,
} from "./API/queries";
import flocknoteActions from "./actions";
import { useFlocknoteAppState } from "./state";

const defaultObj = {};

/**
 * @typedef {object} NetworkMetadata
 * @property {boolean} success - was the api call successful?
 * @property {boolean} hasFNP - does this network have flocknote people?
 * @property {boolean} hasFNC - does this network have flocknote complete? (same backend feature as hasFNP)
 * @property {boolean} hasSignupsProfileFields - Does this network have the ability to add profile fields to signups?
 * @property {boolean} hasUsedFPTrial - has this network ever used the free trial of flocknote people?
 * @property {boolean} hasFundIt - does this network have fundit?
 * @property {boolean} canHaveFundIt - can this network have fundit?
 * @property {boolean} isApp - is this the mobile app?
 * @property {"open"|"closed"} networkType - is it open or closed?
 * @property {boolean} hasAddOnGifts - does the network have the hasAddOnGifts feature flag turned on?
 * @property {boolean} isParent - is this network a parent network?
 * @property {boolean} canAddToParish - can a diocese admin add one of this network's families to a parish? Only ever true for a parent network, and only while household copy is switched on.
 * @property {boolean} canViewLegacyGivingUI - can this network still access classic (legacy) giving?
 * @property {boolean} defaultToNewGivingEnabled - should users default to new giving when they have not yet chosen classic?
 */

/**
 * returns network metadata and a function that initializes the network's metadata.
 *
 * This is intended to fetch and store things that impact multiple compoments such as which features are active on the network (fundIt, flocknotePeople, etc.).
 * @returns {[NetworkMetadata, function]}
 */
const useNetworkMetadata = () => {
  const { data } = useGetNetworkMetadata();
  return [data || defaultObj];
};

/**
 * returns user's data and a function that initializes the user's data.
 *
 * This is intended to fetch and store things that impact multiple compoments such as which features are available to the user.
 * @returns {[object, function]}
 */
const useUserData = () => {
  const { data } = useGetUserData();
  const queryClient = useQueryClient();

  const patchUserData = useCallback(
    (mergeObject) => {
      const currentData = queryClient.getQueryData(flocknoteKeys.user());
      queryClient.setQueryData(
        flocknoteKeys.user(),
        merge(cloneDeep(currentData), mergeObject)
      );
      queryClient.invalidateQueries(flocknoteKeys.user());
    },
    [queryClient]
  );

  return [data || defaultObj, { patchUserData }];
};

/**
 * returns the permissions the current user has on the current network and a way to patch the object.
 *
 * @returns {[UserPermissionsOnNetwork, function]}
 */
const usePermissions = () => {
  const { data } = useGetPermissions();
  const queryClient = useQueryClient();

  const patchPermissions = useCallback(
    (mergeObject) => {
      const currentData = queryClient.getQueryData(flocknoteKeys.permissions());
      queryClient.setQueryData(
        flocknoteKeys.permissions(),
        merge(cloneDeep(currentData), mergeObject)
      );
      queryClient.invalidateQueries(flocknoteKeys.permissions());
    },
    [queryClient]
  );

  return [data || defaultObj, patchPermissions];
};

// This should be initialized on an as needed basis since it will be rare that we need this information. If this changes, it should be moved for UserInfo.
const useFundItPermissions = () => {
  const { data } = useGetUserFundItPermissions();
  return [data || defaultObj];
};

// This should be initialized on an as needed basis since it will be rare that we need this information. If this changes, it should be moved for UserInfo.
const useStaxStatus = () => {
  const [{ staxStatus }, dispatch] = useFlocknoteAppState();
  const [isLoading, setIsLoading] = useState(false);
  const deepStaxStatus = useMemoDeep(() => staxStatus);
  const queryClient = useQueryClient();

  const patch = useCallback(
    (mergeObject) => {
      const clonedStaxStatus = cloneDeep(deepStaxStatus);
      dispatch(
        flocknoteActions.setStaxStatus(merge(clonedStaxStatus, mergeObject))
      );
    },
    [deepStaxStatus, dispatch]
  );

  const init = useCallback(
    (force) => {
      if (!deepStaxStatus || force) {
        setIsLoading(true);
        flocknoteNetworkApi
          .staxStatus()
          .get()
          .then((res) => {
            dispatch(flocknoteActions.setStaxStatus(res));
            window.fnReactBus.setStaxStatus(res);

            // Adding init data to QueryStore so we dont refetch when queries mount (data will always be assumed fresh for app state) - Utilities/ReactQuery/QueryClient.js
            queryClient.setQueryData(flocknoteKeys.staxStatus(), res);
          })
          .catch(() => {})
          .finally(() => {
            setIsLoading(false);
          });
      }
    },
    [dispatch, deepStaxStatus, queryClient]
  );

  return [
    { ...(staxStatus || defaultObj), isLoading },
    { init, patch },
  ];
};

const useFundItStatus = () => {
  const [{ fundItStatus }, dispatch] = useFlocknoteAppState();
  const [isLoading, setIsLoading] = useState(false);
  const queryClient = useQueryClient();

  const init = useCallback(() => {
    if (!fundItStatus) {
      setIsLoading(true);
      flocknoteNetworkApi
        .getFundItStatus()
        .get()
        .then((res) => {
          dispatch(flocknoteActions.setFundItStatus(res));
          window.fnReactBus.setFundItStatus(res);

          // Adding init data to QueryStore so we dont refetch when queries mount (data will always be assumed fresh for app state) - Utilities/ReactQuery/QueryClient.js
          queryClient.setQueryData(flocknoteKeys.fundItStatus(), res);
        })
        .catch((err) => {
          // This handling is leftover from a refactor of fnreact/src/Payments/FundItPage/FundItPage.js
          // TODO: replace alerts with something...not bad...then do redirects with react router.
          if (err.error === "no_admin") {
            // only super admins can access this page.
            window.location = "/action-center";
          } else if (err.error === "invalid_country") {
            alert(
              "We're sorry- only organizations within the U.S. are eligible to sign up for Online Giving."
            );
            window.location = "/action-center";
          } else {
            alert(
              "Oh no! Something went wrong fetching your Online Giving account status. Please try refreshing the page, or contact help@flocknote.com"
            );
          }
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [dispatch, fundItStatus, queryClient]);

  return [{ value: fundItStatus || defaultObj, isLoading }, init];
};

const SHOW_NEW_GIVING_UI_KEY = "showNewGivingUI";

// Every mounted consumer shares one subscription instead of a useState copy of
// localStorage. The choice is written by one component (the archive modal) while
// the giving page and nav are already mounted; with per-component copies those
// keep the old value and the giving page blanks out the moment the network
// becomes classic-eligible again. localStorage stays the source of truth — it is
// read on every render, and www/flocknote.js reads the same key directly.
const showNewGivingUIListeners = new Set();

const subscribeToShowNewGivingUI = (onChange) => {
  showNewGivingUIListeners.add(onChange);
  return () => showNewGivingUIListeners.delete(onChange);
};

// A string (or null) so useSyncExternalStore's snapshot compares by value.
const getStoredShowNewGivingUI = () => {
  try {
    return window.localStorage.getItem(SHOW_NEW_GIVING_UI_KEY);
  } catch (error) {
    return null;
  }
};

const useStoredShowNewGivingUI = () => {
  const raw = useSyncExternalStore(
    subscribeToShowNewGivingUI,
    getStoredShowNewGivingUI,
    () => null
  );

  const stored = useMemo(() => {
    try {
      return raw === null ? null : JSON.parse(raw);
    } catch (error) {
      return null;
    }
  }, [raw]);

  const setShowNewGivingUI = useCallback((value) => {
    try {
      window.localStorage.setItem(
        SHOW_NEW_GIVING_UI_KEY,
        JSON.stringify(value)
      );
    } catch (error) {
      // A full or unavailable localStorage shouldn't break the UI switch; the
      // in-memory notify below still moves every mounted consumer.
    }
    showNewGivingUIListeners.forEach((onChange) => onChange());
  }, []);

  return [stored, setShowNewGivingUI];
};

const useNewGivingUI = () => {
  const { data } = useGetNetworkMetadata();
  const canViewLegacyGivingUI = data?.canViewLegacyGivingUI ?? false;
  const defaultToNewGivingEnabled = data?.defaultToNewGivingEnabled ?? false;

  // `null` means the user has not explicitly chosen a giving experience yet.
  const [storedShowNewGivingUI, setShowNewGivingUI] =
    useStoredShowNewGivingUI();

  // Until the user makes an explicit choice, fall back to the network's
  // configured default (LegacyGiving/DefaultToNewGivingEnabled).
  const showNewGivingUI = canViewLegacyGivingUI
    ? storedShowNewGivingUI ?? defaultToNewGivingEnabled
    : true;

  const hideLegacyGivingUI = !canViewLegacyGivingUI || showNewGivingUI;

  return {
    hideLegacyGivingUI,
    showNewGivingUI,
    setShowNewGivingUI,
    canViewLegacyGivingUI,
  };
};

export {
  useNetworkMetadata,
  useUserData,
  usePermissions,
  useFundItPermissions,
  useFundItStatus,
  useStaxStatus,
  useNewGivingUI,
};
